import Model, { attr, belongsTo, hasMany } from '@warp-drive/legacy/model';
import { Type } from '@warp-drive/core-types/symbols';
import type TasApplicationModel from './tas-application.ts';
import type PcsCompanyModel from './pcs-company.ts';
import type PcsCardHolderModel from './pcs-card-holder.ts';
import type PcsAuthorizationModel from './pcs-authorization.ts';
import type PcsCardEventModel from './pcs-card-event.ts';
import type PcsDisputeModel from './pcs-dispute.ts';
import type PcsTransactionModel from './pcs-transaction.ts';
import type PcsProviderModel from './pcs-provider.ts';
import type PcsProgramModel from './pcs-program.ts';

export interface IssuingCard {
  id: string;
  type: 'virtual' | 'physical'; // Stripe allows both
  last4: string;
  object: 'issuing.card';
  status: 'active' | 'inactive' | 'canceled' | string; // extend as needed
  created: string; // ISO timestamp
  livemode: boolean;

  wallets: {
    apple_pay?: {
      eligible: boolean;
      ineligible_reason?: string | null;
    };
    google_pay?: {
      eligible: boolean;
      ineligible_reason?: string | null;
    };
    primary_account_identifier: string | null;
  };

  metadata?: Record<string, string>;

  cardholder: {
    id: string;
  };

  spending_controls?: {
    spending_limits: {
      amount: number; // e.g., 1000000
      interval: 'per_authorization' | 'daily' | 'weekly' | 'monthly' | string;
      categories: string[];
    }[];
    allowed_categories: string[] | null;
    blocked_categories: string[] | null;
    spending_limits_currency: string | null; // e.g., "usd"
    allowed_merchant_countries: string[] | null;
    blocked_merchant_countries: string[] | null;
  };

  cancellation_reason: string | null;
}

export interface PcsCardAmounts {
  authorization: {
    approved: number;
    captured: number;
    created: number;
    declined: number;
    expired: number;
    requested: number;
    reversed: number;
  };
  dispute: {
    created: number;
  };
  program: {
    approved: number;
  };
  transaction: {
    captured: number;
    created: number;
    refunded: number;
  };
}

export default class PcsCardModel extends Model {
  @attr declare state: string;
  @attr declare program: string;
  @attr declare providerId: string;
  @attr declare providerData: IssuingCard;
  @attr declare amounts: PcsCardAmounts;

  /*************************
   **  Relationships      **
   *************************/

  @belongsTo('pcs-card-holder', { async: false, inverse: 'pcsCards' })
  declare pcsCardHolder: PcsCardHolderModel | null;

  @belongsTo('tas-application', { async: false, inverse: 'pcsCards' })
  declare tasApplication: TasApplicationModel;

  @belongsTo('pcs-company', { async: false, inverse: 'pcsCards' })
  declare pcsCompany: PcsCompanyModel;

  @belongsTo('pcs-provider', { async: false, inverse: 'pcsCards' })
  declare pcsProvider: PcsProviderModel;

  @belongsTo('pcs-program', { async: false, inverse: 'pcsCards' })
  declare pcsProgram: PcsProgramModel;

  @hasMany('pcs-authorization', { async: false, inverse: 'pcsCard' })
  declare pcsAuthorizations: PcsAuthorizationModel[];

  @hasMany('pcs-card-event', { async: false, inverse: 'pcsCard' })
  declare pcsCardEvents: PcsCardEventModel[];

  @hasMany('pcs-dispute', { async: false, inverse: 'pcsCard' })
  declare pcsDisputes: PcsDisputeModel[];

  @hasMany('pcs-transaction', { async: false, inverse: 'pcsCard' })
  declare pcsTransactions: PcsTransactionModel[];

  /*************************
   **  Computed           **
   *************************/

  get isActive() {
    return this.state === 'PCS.CardState.ACTIVATED';
  }

  get isDeactivated() {
    return this.state === 'PCS.CardState.INACTIVATED' || this.state === 'PCS.CardState.CANCELED';
  }

  [Type] = 'pcs-card' as const;
}
